import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import { Button, Chip } from '@/components/atoms';
import { Menu, MenuItem } from '@/components/atoms/Menu';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { FilterProps, SelectedFilter } from './Filter.props';

/**
 * Deep equality comparison for SelectedFilter arrays
 * More efficient and reliable than JSON.stringify
 */
const areFiltersEqual = (a: SelectedFilter[], b: SelectedFilter[]): boolean => {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const filterA = a[i];
    const filterB = b[i];

    // Compare column names
    if (filterA.column !== filterB.column) return false;

    // Compare selectedValues arrays
    if (filterA.selectedValues.length !== filterB.selectedValues.length) {
      return false;
    }

    // Compare each value in selectedValues
    for (let j = 0; j < filterA.selectedValues.length; j++) {
      if (filterA.selectedValues[j] !== filterB.selectedValues[j]) {
        return false;
      }
    }
  }

  return true;
};

export const Filter: React.FC<FilterProps> = ({
  columnMappings,
  filters,
  value = [],
  defaultFilters = [],
  onFiltersChange,
  sx = {},
}) => {
  const theme = useTheme();
  const [addFilterAnchorEl, setAddFilterAnchorEl] =
    useState<null | HTMLElement>(null);
  const [filterValueAnchorEl, setFilterValueAnchorEl] =
    useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [selectedFilters, setSelectedFilters] =
    useState<SelectedFilter[]>(value);
  const [columnToAutoOpen, setColumnToAutoOpen] = useState<string | null>(null);
  const isAutoOpeningRef = useRef(false);
  const filterButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const prevValueRef = useRef<SelectedFilter[]>(value);
  const filterValueTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const addFilterOpen = Boolean(addFilterAnchorEl);
  const filterValueOpen = Boolean(filterValueAnchorEl);

  // Use ref to hold latest callback without adding to dependencies
  const onFiltersChangeRef = useRef(onFiltersChange);
  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange;
  }, [onFiltersChange]);

  useEffect(() => {
    if (onFiltersChangeRef.current) {
      onFiltersChangeRef.current(selectedFilters);
    }
  }, [selectedFilters]);

  // Cleanup pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (filterValueTimeoutRef.current) {
        clearTimeout(filterValueTimeoutRef.current);
      }
    };
  }, []);

  // Sync internal state when value prop changes (from Redux or parent)
  // Only update if value actually changed (not just a re-render)
  useEffect(() => {
    const hasChanged = !areFiltersEqual(prevValueRef.current, value);
    if (hasChanged) {
      setSelectedFilters(value);
      prevValueRef.current = value;
    }
  }, [value]);

  // Auto-open menu for newly added empty filter
  useEffect(() => {
    if (!columnToAutoOpen) return;

    const timeoutIds: NodeJS.Timeout[] = [];

    const filterButton = filterButtonRefs.current[columnToAutoOpen];
    if (!filterButton) {
      setColumnToAutoOpen(null);
      isAutoOpeningRef.current = false;
      return;
    }

    // Use a small delay to ensure the add filter menu click event completes
    // and the DOM is fully updated before opening the new menu
    const initialTimeoutId = setTimeout(() => {
      isAutoOpeningRef.current = true;
      setActiveFilterColumn(columnToAutoOpen);

      // Scroll to center the newly added filter before opening the menu
      const scrollContainer = scrollRef.current;
      if (scrollContainer) {
        // Calculate the position to scroll so the filter is centered
        const containerWidth = scrollContainer.offsetWidth;
        const filterButtonLeft = filterButton.offsetLeft;
        const filterButtonWidth = filterButton.offsetWidth;
        const targetScroll =
          filterButtonLeft + filterButtonWidth / 2 - containerWidth / 2;

        scrollContainer.scrollTo({
          left: Math.max(0, targetScroll),
          behavior: 'smooth',
        });

        // Open the menu after scroll completes
        const menuTimeoutId = setTimeout(() => {
          setFilterValueAnchorEl(filterButton);
          setColumnToAutoOpen(null);
        }, 200);
        timeoutIds.push(menuTimeoutId);
      } else {
        setFilterValueAnchorEl(filterButton);
        setColumnToAutoOpen(null);
      }

      // Reset the flag after menu stabilizes
      const resetTimeoutId = setTimeout(() => {
        isAutoOpeningRef.current = false;
      }, 150);
      timeoutIds.push(resetTimeoutId);
    }, 50);
    timeoutIds.unshift(initialTimeoutId);

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [columnToAutoOpen]);

  const handleAddFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAddFilterAnchorEl(event.currentTarget);
  };

  const handleAddFilterClose = () => {
    setAddFilterAnchorEl(null);
  };

  const handleFilterValueClick = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    // Clear any pending timeout from previous calls
    if (filterValueTimeoutRef.current) {
      clearTimeout(filterValueTimeoutRef.current);
    }

    const scrollContainer = scrollRef.current;
    const filterElement = event.currentTarget;

    if (scrollContainer && filterElement) {
      // Calculate the position to scroll so the filter is centered
      const containerWidth = scrollContainer.offsetWidth;
      const filterElementLeft = filterElement.offsetLeft;
      const filterElementWidth = filterElement.offsetWidth;
      const targetScroll =
        filterElementLeft + filterElementWidth / 2 - containerWidth / 2;

      scrollContainer.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth',
      });

      // Open menu with slight delay to allow scroll to complete
      filterValueTimeoutRef.current = setTimeout(() => {
        setFilterValueAnchorEl(filterElement);
        setActiveFilterColumn(column);
        filterValueTimeoutRef.current = null;
      }, 200);
    } else {
      setFilterValueAnchorEl(event.currentTarget);
      setActiveFilterColumn(column);
    }
  };

  const handleFilterValueClose = (skipEmptyCheck = false) => {
    // If the menu is closing and the filter has no selected values, remove it
    // Skip this check if a value was just selected (to avoid race condition with state update)
    if (activeFilterColumn && !skipEmptyCheck) {
      const filter = selectedFilters.find(
        (f) => f.column === activeFilterColumn
      );
      if (filter && filter.selectedValues.length === 0) {
        setSelectedFilters(
          selectedFilters.filter((f) => f.column !== activeFilterColumn)
        );
      }
    }
    setFilterValueAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnSelect = (column: string) => {
    // Check if column is already selected
    const isSelected = selectedFilters.some((f) => f.column === column);

    if (isSelected) {
      // Remove the filter
      setSelectedFilters(selectedFilters.filter((f) => f.column !== column));
      handleAddFilterClose();
    } else {
      // Add the filter with empty selectedValues (no default selection)
      setSelectedFilters([...selectedFilters, { column, selectedValues: [] }]);
      handleAddFilterClose();

      // Set the column to auto-open - useEffect will handle opening the menu
      setColumnToAutoOpen(column);
    }
  };

  // Ref callback to store references to filter buttons
  const setFilterButtonRef = useCallback(
    (column: string, element: HTMLElement | null) => {
      if (element) {
        filterButtonRefs.current[column] = element;
      } else {
        delete filterButtonRefs.current[column];
      }
    },
    []
  );

  const handleValueSelect = (column: string, value: string) => {
    const filterOption = filters.find((f) => f.column === column);
    const filterType = filterOption?.type || 'single';

    setSelectedFilters(
      selectedFilters.map((f) => {
        if (f.column === column) {
          if (filterType === 'multi') {
            // Toggle value in multi-select
            const isSelected = f.selectedValues.includes(value);
            const newValues = isSelected
              ? f.selectedValues.filter((v) => v !== value)
              : [...f.selectedValues, value];
            return { ...f, selectedValues: newValues };
          } else {
            // Replace value in single-select
            return { ...f, selectedValues: [value] };
          }
        }
        return f;
      })
    );

    // Close menu only for single-select
    // Pass true to skip empty check since we just selected a value
    if (filterType === 'single') {
      handleFilterValueClose(true);
    }
  };

  const handleRemoveValue = (
    column: string,
    value: string,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }

    setSelectedFilters(
      selectedFilters
        .map((f) => {
          if (f.column === column) {
            const newValues = f.selectedValues.filter((v) => v !== value);
            // If no values left, return null to remove the filter
            if (newValues.length === 0) {
              return null;
            }
            return { ...f, selectedValues: newValues };
          }
          return f;
        })
        .filter((f) => f !== null) as SelectedFilter[]
    );
  };

  const isColumnSelected = (column: string) => {
    return selectedFilters.some((f) => f.column === column);
  };

  const handleReset = () => {
    setSelectedFilters(defaultFilters);
  };

  // Scrollable selected filters logic
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateIcons = () => {
      const hasOverflow = el.scrollWidth > el.offsetWidth;
      setShowLeft(hasOverflow && el.scrollLeft > 0);
      setShowRight(
        hasOverflow && el.scrollLeft + el.offsetWidth < el.scrollWidth - 1
      );
    };
    updateIcons();
    el.addEventListener('scroll', updateIcons);
    window.addEventListener('resize', updateIcons);

    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateIcons();
    });
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateIcons);
      window.removeEventListener('resize', updateIcons);
      resizeObserver.disconnect();
    };
  }, [selectedFilters]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.offsetWidth / 2;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        gap: theme.unit(2),
        minHeight: '42px',
        minWidth: 0,
        ...sx,
      }}
    >
      {/* Reset Button */}
      <Button
        variant="outlined"
        size="m"
        onClick={handleReset}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
        }}
      >
        Reset
      </Button>

      {/* Add Filter Button */}
      <Button
        id="add-filter-trigger"
        aria-controls={addFilterOpen ? 'add-filter-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={addFilterOpen ? 'true' : undefined}
        variant="outlined"
        onClick={handleAddFilterClick}
        startIcon={<AddIcon />}
        size="m"
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          borderRadius: theme.unit(2.5),
          color: theme.palette.M3.sys.light['on-surface-variant'],
          ...theme.typography.M3.body.mediumProminent,
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['background 2'],
            borderColor: theme.palette.M3.sys.light['outline 3'],
          },
          '&:focus': {
            backgroundColor: theme.palette.M3.sys.light['background 2'],
            borderColor: theme.palette.M3.sys.light['outline 3'],
            color: theme.palette.M3.sys.light['on-surface-variant'],
          },
          '&[aria-expanded="true"]': {
            backgroundColor: theme.palette.M3.sys.light['background 2'],
            borderColor: theme.palette.M3.sys.light['outline 3'],
            color: theme.palette.M3.sys.light['on-surface-variant'],
          },
        }}
      >
        Filter
      </Button>

      {/* Add Filter Menu */}
      <Menu
        id="add-filter-menu"
        anchorEl={addFilterAnchorEl}
        open={addFilterOpen}
        onClose={handleAddFilterClose}
        slotProps={{
          paper: {
            sx: {
              maxHeight: theme.unit(76.5),
              width: 'unset',
              minWidth: theme.unit(50),
            },
          },
          list: {
            'aria-labelledby': 'add-filter-trigger',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          top: theme.unit(1),
        }}
      >
        {filters.map((filterOption) => {
          const label =
            columnMappings[filterOption.column] || filterOption.column;
          const selected = isColumnSelected(filterOption.column);

          return (
            <MenuItem
              key={filterOption.column}
              onClick={() => handleColumnSelect(filterOption.column)}
              sx={{
                backgroundColor: selected
                  ? theme.palette.M3.sys.light['secondary-container']
                  : 'transparent',
                color: theme.palette.M3.sys.light['on-surface'],
              }}
            >
              {label}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Scrollable selected filters area */}
      {selectedFilters.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showLeft && (
            <Box
              onClick={() => scroll('left')}
              sx={{
                position: 'absolute',
                left: 0,
                zIndex: 2,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.M3.sys.light['on-surface-variant'],
                background: `linear-gradient(to right, ${theme.palette.M3.white} 80%, transparent)`,
                cursor: 'pointer',
                px: 1,
              }}
            >
              <KeyboardArrowLeftIcon />
            </Box>
          )}
          <Box
            ref={scrollRef}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              flex: 1,
              minHeight: '38px',
              display: 'flex',
              gap: theme.unit(2),
              alignItems: 'center',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {selectedFilters.map((filter) => {
              const label = columnMappings[filter.column] || filter.column;
              const filterOption = filters.find(
                (f) => f.column === filter.column
              );
              const isCurrentFilterOpen =
                filterValueOpen && activeFilterColumn === filter.column;
              const firstValue = filter.selectedValues[0];
              const remainingCount = filter.selectedValues.length - 1;
              return (
                <React.Fragment key={filter.column}>
                  <Box
                    ref={(el) =>
                      setFilterButtonRef(
                        filter.column,
                        el as HTMLElement | null
                      )
                    }
                    onClick={(e) => handleFilterValueClick(e, filter.column)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.unit(1),
                      padding: theme.unit(1.25, 3),
                      backgroundColor: 'transparent',
                      borderRadius: theme.unit(2.5),
                      border: '1px solid transparent',
                      cursor: 'pointer',
                      flex: '0 0 auto',
                      minWidth: 'max-content',
                      '&:hover': {
                        backgroundColor:
                          theme.palette.M3.sys.light['background 2'],
                        border: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
                        '& .MuiChip-root': {
                          backgroundColor: theme.palette.M3.white,
                        },
                      },
                      '&:focus': {
                        backgroundColor:
                          theme.palette.M3.sys.light['background 2'],
                        border: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
                        outline: 'none',
                        '& .MuiChip-root': {
                          backgroundColor: theme.palette.M3.white,
                        },
                      },
                      ...(isCurrentFilterOpen && {
                        backgroundColor:
                          theme.palette.M3.sys.light['background 2'],
                        border: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
                        '& .MuiChip-root': {
                          backgroundColor: theme.palette.M3.white,
                        },
                      }),
                    }}
                  >
                    <FilterListIcon
                      sx={{
                        fontSize: '18px',
                        color: theme.palette.M3.sys.light['on-surface-variant'],
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        ...theme.typography.M3.body.mediumProminent,
                        color: theme.palette.M3.sys.light['on-surface-variant'],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: '0 1 auto',
                        minWidth: '60px',
                      }}
                    >
                      {label}
                    </Box>
                    {firstValue && (
                      <Chip
                        label={firstValue}
                        size="s"
                        onDelete={(e) => {
                          handleRemoveValue(filter.column, firstValue, e);
                        }}
                        deleteIcon={<CloseIcon />}
                        sx={{
                          height: '26px',
                          padding: theme.unit(0.5, 1.5),
                          backgroundColor:
                            theme.palette.M3.sys.light['background'],
                          border: 'none',
                          flex: '0 1 auto',
                          minWidth: '80px',
                          maxWidth: '200px',
                          '& .MuiChip-label': {
                            ...theme.typography.M3.label.mediumProminent,
                            color:
                              theme.palette.M3.sys.light['on-surface-variant'],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          },
                          '& .MuiChip-deleteIcon': {
                            fontSize: '18px !important',
                            width: '18px !important',
                            height: '18px !important',
                            color:
                              theme.palette.M3.sys.light['on-surface-variant'],
                            margin: 'unset',
                            flexShrink: 0,
                          },
                          '& .MuiChip-deleteIcon:hover': {
                            color: theme.palette.State.error,
                          },
                        }}
                      />
                    )}
                    {remainingCount > 0 && (
                      <Chip
                        label={`+${remainingCount}`}
                        size="s"
                        sx={{
                          height: '26px',
                          padding: theme.unit(1, 2),
                          backgroundColor:
                            theme.palette.M3.sys.light['background'],
                          border: 'none',
                          flexShrink: 0,
                          '& .MuiChip-label': {
                            ...theme.typography.M3.label.mediumProminent,
                            color:
                              theme.palette.M3.sys.light['on-surface-variant'],
                          },
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        flexShrink: 0,
                      }}
                    >
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: '18px',
                          color:
                            theme.palette.M3.sys.light['on-surface-variant'],
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Filter Value Menu */}
                  <Menu
                    anchorEl={isCurrentFilterOpen ? filterValueAnchorEl : null}
                    open={isCurrentFilterOpen}
                    onClose={() => handleFilterValueClose(false)}
                    slotProps={{
                      paper: {
                        sx: {
                          maxHeight: theme.unit(76.5),
                          width: 'unset',
                          minWidth: theme.unit(50),
                        },
                      },
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      top: theme.unit(1),
                    }}
                  >
                    {filterOption?.values
                      .filter((item) => item.value !== '')
                      .map((item) => {
                        const value = item.value;
                        const count = item.count;
                        const isSelected =
                          filter.selectedValues.includes(value);
                        return (
                          <MenuItem
                            key={value}
                            onClick={() =>
                              handleValueSelect(filter.column, value)
                            }
                            sx={{
                              backgroundColor: isSelected
                                ? theme.palette.M3.sys.light[
                                    'secondary-container'
                                  ]
                                : 'transparent',
                              display: 'flex',
                              gap: theme.unit(2),
                              alignItems: 'center',
                            }}
                          >
                            {value}
                            {count && (
                              <Chip
                                label={count.toString()}
                                sx={{
                                  border: `1px solid ${theme.palette.M3.sys.light['outline 2']}`,
                                }}
                              />
                            )}
                          </MenuItem>
                        );
                      })}
                  </Menu>
                </React.Fragment>
              );
            })}
          </Box>
          {showRight && (
            <Box
              onClick={() => scroll('right')}
              sx={{
                position: 'absolute',
                right: 0,
                zIndex: 2,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.M3.sys.light['on-surface-variant'],
                background: `linear-gradient(to left, ${theme.palette.M3.white} 80%, transparent)`,
                cursor: 'pointer',
                px: 1,
              }}
            >
              <KeyboardArrowRightIcon />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Filter;
